import { autoinject, transient } from "aurelia-dependency-injection";
import { useView } from "aurelia-framework";
import { MepInsightsFilter } from "./partials/mep-insights-filter/mep-insights-filter";
import { MepInsightsRequest } from "../../../../../../models/mep/mep-insights.models";
import { MepLookupService } from "../../../../../../services/mep/mep-lookup.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { MepInsightsContributorsToEmissionsViewModel } from "./partials/mep-insights-top-contributors-to-emissions/mep-insights-top-contributors-to-emissions";
import { MepInsightsCorrelationWithPortTimeCalls } from "./partials/mep-correlation-with-port-time-calls/mep-insights-correlation-with-port-time-calls";
import { MepInsightsService } from "../../../../../../services/mep/mep-insights.service";
import { MepInsightsVesselEmissionsComparison } from "./partials/mep-insights-vessel-emissions-comparison/mep-insights-vessel-emissions-comparison";
import { activationStrategy } from "aurelia-router";
import { MepInsightsEmissionsByMonth } from "./partials/mep-insights-emissions-by-month/mep-insights-emissions-by-month";
import { BaseMepFilterablePage, MepPortDetails } from "../../../../../../models/mep/mep.models";
import { MepService } from "../../../../../../services/mep/mep.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { MepInsightsLongTermForecast } from "./partials/mep-insights-long-term-forecast/mep-insights-long-term-forecast";
import { CustomerDetailsModel } from "../../../../../../models/customers/customer.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject
@transient()
@useView('views/pages/modules/mep/tabs/mep-insights/mep-insights.html')
export class MepInsights extends BaseMepFilterablePage<MepInsightsFilter> {

    public portId: number;
    public topContributorsToEmissionsViewModel: MepInsightsContributorsToEmissionsViewModel;
    public smallestContributorsToEmissionsViewModel: MepInsightsContributorsToEmissionsViewModel;
    public vesselEmissionsComparisonViewModel: MepInsightsVesselEmissionsComparison;
    public correlationWithPortTimeCalls: MepInsightsCorrelationWithPortTimeCalls;
    public correlationWithPortTimeCallsViewModelForPrinting: MepInsightsCorrelationWithPortTimeCalls;
    public correlationWithPortTimeCallsViewModelForPrintingPortCalls: MepInsightsCorrelationWithPortTimeCalls;
    public emissionsByMonthViewModel: MepInsightsEmissionsByMonth;
    public longTermForecastViewModel: MepInsightsLongTermForecast;
    public portDetails: MepPortDetails;
    public supportsCargoType: boolean;

    //Claims
    canGeneratePortDashboardReport: boolean;

    refreshDataFunc: (request: MepInsightsRequest) => Promise<string[]>;
    public routeParams: any;

    customerDetails: CustomerDetailsModel;

    constructor(private notificationService: NotificationClientService, private mepLookupService: MepLookupService,
        private userClaimsService: UserClaimsService, private mepInsightsService: MepInsightsService,
        private mepService: MepService, private dialogClientService: DialogClientService,
        private eventAggregator: EventAggregator,
        private notificationClientService: NotificationClientService, private configService: ConfigService) {
        super();
        this.refreshDataFunc = (request: MepInsightsRequest) => this.loadInsightsData(request);
        this.canGeneratePortDashboardReport = userClaimsService.hasPermission(Claims.CanGeneratePortDashboardReport);
    }

    activate(param) {
        this.portId = param.portId;
        this.supportsCargoType = param.supportsCargoType;
        if (!this.filterViewModel)
            this.initializeFilterVM();
        this.customerDetails = CustomerDetailsModel.getCustomerDetailModelFromRSUser(window["rightshipUser"]);
        const isMepTrial = this.customerDetails && this.customerDetails.isMepTrial;
        this.topContributorsToEmissionsViewModel = new MepInsightsContributorsToEmissionsViewModel(this.mepInsightsService, this.eventAggregator, true, isMepTrial);
        this.smallestContributorsToEmissionsViewModel = new MepInsightsContributorsToEmissionsViewModel(this.mepInsightsService, this.eventAggregator, false, isMepTrial);
        this.vesselEmissionsComparisonViewModel = new MepInsightsVesselEmissionsComparison(this.mepInsightsService, this.eventAggregator, isMepTrial, this.configService);
        this.correlationWithPortTimeCalls = new MepInsightsCorrelationWithPortTimeCalls(this.mepInsightsService, this.filterViewModel, isMepTrial);
        this.emissionsByMonthViewModel = new MepInsightsEmissionsByMonth(this.mepInsightsService, this.eventAggregator, isMepTrial, this.configService);
        this.longTermForecastViewModel = new MepInsightsLongTermForecast(this.mepInsightsService, this.eventAggregator, isMepTrial, this.configService);

        this.routeParams = param;
        if (param && param.year) {
            this.filterViewModel.selectedYear = param.year;
        }
    }

    initializeFilterVM(model?): MepInsightsFilter {
        this.filterViewModel = new MepInsightsFilter(model && model.portId ? model.portId : this.portId, this.supportsCargoType, this.refreshDataFunc, this.notificationService, this.mepLookupService, this.userClaimsService);
        return this.filterViewModel;
    }

    attached() {
        this.filterViewModel.initLookups().then(() => {
            this.filterViewModel.setFiltersFromParams(this.routeParams)
        }).catch(() => {
            this.notificationService.show("MEP Error", "Error loading data for selected year!", NotificationType.Error);
        });

        this.mepService.getMepPortDetails(this.portId, true).then(x => {
            this.portDetails = x;
        }).catch(() => {
            this.notificationService.show("MEP Error", "Error loading port details!", NotificationType.Error);
        });
    }

    loadInsightsData(request: MepInsightsRequest): Promise<string[]> {
        if (!this.topContributorsToEmissionsViewModel ||
            !this.smallestContributorsToEmissionsViewModel ||
            !this.vesselEmissionsComparisonViewModel ||
            !this.correlationWithPortTimeCalls ||
            !this.emissionsByMonthViewModel ||
            !this.longTermForecastViewModel) {
            return Promise.reject();
        }

        this.clearInsightsData();
        const promises = [];

        promises.push(this.topContributorsToEmissionsViewModel.getContributorsToEmissions(request));
        promises.push(this.smallestContributorsToEmissionsViewModel.getContributorsToEmissions(request));
        promises.push(this.vesselEmissionsComparisonViewModel.getMepInsightsVesselEmissionsComparedWithTarget(request));
        promises.push(this.correlationWithPortTimeCalls.getCorellationWithPortCallsAndTime(request));
        promises.push(this.emissionsByMonthViewModel.getInsightsEmissionsByMonth(request));
        promises.push(this.longTermForecastViewModel.getLongTermForecast(request));

        return Promise.all(promises);
    }

    clearInsightsData() {
        this.topContributorsToEmissionsViewModel.clearContributorsData();
        this.smallestContributorsToEmissionsViewModel.clearContributorsData();
        this.vesselEmissionsComparisonViewModel.clearVesselEmissionsComparisonData();
        this.emissionsByMonthViewModel.clearEmissionsByMonth();
        this.correlationWithPortTimeCalls.clearCorrelationData();
        this.longTermForecastViewModel.clearLongTermForecastData();
    }

    generateReport() {
        if (!this.filterViewModel.isRefreshingData) {

            const isMepTrial = this.customerDetails && this.customerDetails.isMepTrial;

            //Duplicate VM for printing purpose
            this.correlationWithPortTimeCallsViewModelForPrinting = new MepInsightsCorrelationWithPortTimeCalls(this.mepInsightsService, this.filterViewModel, isMepTrial);
            this.correlationWithPortTimeCallsViewModelForPrintingPortCalls = new MepInsightsCorrelationWithPortTimeCalls(this.mepInsightsService, this.filterViewModel, isMepTrial);
            this.correlationWithPortTimeCallsViewModelForPrinting.isTypeSelected = false;
            this.correlationWithPortTimeCallsViewModelForPrintingPortCalls.isTypeSelected = true;
            this.correlationWithPortTimeCallsViewModelForPrinting.correlationData = JSON.parse(JSON.stringify(this.correlationWithPortTimeCalls.correlationData));
            this.correlationWithPortTimeCallsViewModelForPrintingPortCalls.correlationData = JSON.parse(JSON.stringify(this.correlationWithPortTimeCalls.correlationData));
            this.correlationWithPortTimeCallsViewModelForPrinting.initCorrelationChart();
            this.correlationWithPortTimeCallsViewModelForPrintingPortCalls.initCorrelationChart();

            //Show dialog
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PrintMepInsightsDialog, this, true, false, true);
        }
        else {
            this.notificationClientService.show("Page is loading, please try again once its loaded", "", NotificationType.Info);
        }
    }

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    refresh(data?: { portId: number, year: number, supportsCargoType: boolean }) {
        if (this.portDetails) {
            this.filterViewModel.clearAllFilters(false);
            if (data && data.portId) {
                this.portId = data.portId;
                this.filterViewModel.portId = data.portId;
                this.filterViewModel.supportsCargoType = data.supportsCargoType;
            }

            if (data && data.year) {
                this.filterViewModel.selectedYear = data.year;
            }

            this.filterViewModel.initLookups().then(() => {
                this.mepService.getMepPortDetails(this.portId, true).then(x => {
                    this.portDetails = x;
                    this.filterViewModel.applyFilters();
                }).catch(() => {
                    this.notificationClientService.show("MEP Error", "Error loading port details!", NotificationType.Error);
                });
            });
        }
    }
}
